import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, FileText, Download, BarChart3, FileSpreadsheet, Calendar } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface Report {
  id: string
  name: string
  type: string
  format: string
  status: string
  fileSize: number | null
  downloadUrl: string | null
  createdAt: string
}

function typeBadge(type: string) {
  switch (type) {
    case 'executive': return <Badge variant="default">Executive</Badge>
    case 'technical': return <Badge variant="info">Technical</Badge>
    case 'compliance': return <Badge variant="warning">Compliance</Badge>
    default: return <Badge variant="secondary">{type}</Badge>
  }
}

function formatBadge(format: string) {
  switch (format) {
    case 'pdf': return <Badge variant="outline" className="text-red-500 border-red-500/30">PDF</Badge>
    case 'excel': return <Badge variant="outline" className="text-green-500 border-green-500/30">Excel</Badge>
    case 'csv': return <Badge variant="outline">CSV</Badge>
    default: return <Badge variant="outline">{format}</Badge>
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'completed': return <Badge variant="success">Completed</Badge>
    case 'generating': return <Badge variant="info">Generating</Badge>
    case 'pending': return <Badge variant="secondary">Pending</Badge>
    case 'failed': return <Badge variant="destructive">Failed</Badge>
    default: return <Badge variant="secondary">{status}</Badge>
  }
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function GenerateReportDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState('executive')
  const [format, setFormat] = useState('pdf')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/reports', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      toast({ title: 'Report queued', description: 'Report generation started' })
      setOpen(false)
      setName('')
      setDateFrom('')
      setDateTo('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to generate report', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Generate Report</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
          <DialogDescription>Create a new report with the selected parameters.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Report Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Monthly Security Summary" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="executive">Executive</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              name,
              type,
              format,
              dateFrom: dateFrom || undefined,
              dateTo: dateTo || undefined,
            })}
            disabled={!name || createMutation.isPending}
          >
            {createMutation.isPending ? 'Generating...' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ReportingPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [formatFilter, setFormatFilter] = useState<string>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['reports', typeFilter, formatFilter],
    queryFn: () => apiClient.get<{ reports: Report[]; pagination: unknown }>('/reports', {
      ...(typeFilter !== 'all' && { type: typeFilter }),
      ...(formatFilter !== 'all' && { format: formatFilter }),
    }),
  })

  const reports = data?.data?.reports || []
  const filtered = reports.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))

  const now = new Date()
  const thisMonth = reports.filter((r) => {
    const d = new Date(r.createdAt)
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  }).length

  const pdfCount = reports.filter((r) => r.format === 'pdf').length
  const excelCount = reports.filter((r) => r.format === 'excel').length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">Generate and download security and compliance reports</p>
        </div>
        <GenerateReportDialog />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-muted-foreground" /><p className="text-xs text-muted-foreground">Total Reports</p></div><div className="text-2xl font-bold mt-1">{reports.length}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><FileText className="h-4 w-4 text-red-500" /><p className="text-xs text-muted-foreground">PDF Reports</p></div><div className="text-2xl font-bold mt-1">{pdfCount}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-green-500" /><p className="text-xs text-muted-foreground">Excel Reports</p></div><div className="text-2xl font-bold mt-1">{excelCount}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /><p className="text-xs text-muted-foreground">This Month</p></div><div className="text-2xl font-bold mt-1">{thisMonth}</div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search reports..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="executive">Executive</SelectItem>
            <SelectItem value="technical">Technical</SelectItem>
            <SelectItem value="compliance">Compliance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={formatFilter} onValueChange={setFormatFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Formats</SelectItem>
            <SelectItem value="pdf">PDF</SelectItem>
            <SelectItem value="excel">Excel</SelectItem>
            <SelectItem value="csv">CSV</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No reports found</p>
            <p className="text-sm text-muted-foreground">Generate your first report to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_100px_80px_100px_120px_80px_60px] gap-4 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span>Name</span>
            <span>Type</span>
            <span>Format</span>
            <span>Status</span>
            <span>Created</span>
            <span>Size</span>
            <span></span>
          </div>
          {filtered.map((report) => (
            <Card key={report.id}>
              <CardContent className="grid grid-cols-[1fr_100px_80px_100px_120px_80px_60px] gap-4 items-center p-4">
                <span className="font-medium truncate">{report.name}</span>
                <span>{typeBadge(report.type)}</span>
                <span>{formatBadge(report.format)}</span>
                <span>{statusBadge(report.status)}</span>
                <span className="text-sm text-muted-foreground">{new Date(report.createdAt).toLocaleDateString()}</span>
                <span className="text-sm text-muted-foreground">{formatFileSize(report.fileSize)}</span>
                <span>
                  {report.status === 'completed' && report.downloadUrl && (
                    <Button variant="ghost" size="icon" asChild>
                      <a href={report.downloadUrl} download>
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
